import { fetchAuthSession } from 'aws-amplify/auth';

const BASE = ((import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api').replace(/\/$/, '');
const REQUEST_TIMEOUT_MS = 30_000;

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}`);
  }
}

async function getToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch {
    return undefined;
  }
}

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();

  const res = await fetchWithTimeout(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401) {
    // Token may have expired — try once to refresh, then sign out
    try {
      const refreshed = await fetchAuthSession({ forceRefresh: true });
      const newToken = refreshed.tokens?.idToken?.toString();
      if (newToken) {
        const retryRes = await fetchWithTimeout(`${BASE}${path}`, {
          ...init,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${newToken}`,
            ...(init?.headers ?? {}),
          },
        });
        if (!retryRes.ok) {
          const body = await retryRes.json().catch(() => ({}));
          throw new ApiError(retryRes.status, body);
        }
        return retryRes.json() as Promise<T>;
      }
    } catch (refreshErr) {
      if (refreshErr instanceof ApiError) throw refreshErr;
    }
    // Import lazily to avoid circular dependency
    const { useAuthStore } = await import('@/store/authStore');
    await useAuthStore.getState().signOut();
    throw new ApiError(401, { error: 'Session expired' });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  return res.json() as Promise<T>;
}
